import { useUnit } from 'effector-react';
import { generateUuid } from '@lib/uuid';
import { useChanges } from '@nx-frontend/shared/hooks/useChangeEvents';
import { $orgId, $workspaceId } from '@nx-frontend/shared/store/portal-app';
import { $virtualNodePayload, createPagesFromNimbusContentFx } from '@nx-frontend/shared/store/virtual-portal-node';
import { ContentBlock, MenuItem, MenuItemType, VirtualPortalNode } from '@nx-frontend/shared/types';
import { ChangeEventType } from '@nx-frontend/shared/types';

interface NimbusNodesCreatePayload {
  items: MenuItem[];
}

export const useNimbusNodesCreate = (): ((
  payload: NimbusNodesCreatePayload
) => Promise<{ nodes: VirtualPortalNode[]; menuItems: MenuItem[] }>) => {
  const [pagePayload, orgId, workspaceId, createPagesFromNimbusContent] = useUnit([
    $virtualNodePayload,
    $orgId,
    $workspaceId,
    createPagesFromNimbusContentFx,
  ]);
  const setChanges = useChanges();

  return async ({ items }: NimbusNodesCreatePayload) => {
    if (!pagePayload) {
      throw new Error('Missing node payload');
    }

    const ids = items.map((menuItem) => menuItem.id);
    const { nodes, menuItems, blocks } = await createPagesFromNimbusContent({
      ids,
      menuType: pagePayload.menuType,
      parentId: pagePayload.parentId,
      orgId,
      workspaceId,
    });

    const batchId = generateUuid();
    nodes.forEach((node) => {
      setChanges({
        type: ChangeEventType.ADD_NODE,
        batchId,
        oldValue: undefined,
        newValue: node,
      });
    });

    menuItems.forEach((menuItem) => {
      const preparedMenuItem = menuItem.type === MenuItemType.NOTES_FOLDER ? { ...menuItem, expanded: true } : menuItem;
      setChanges({
        type: ChangeEventType.ADD_MENU_ITEM,
        batchId,
        oldValue: undefined,
        newValue: { menuType: pagePayload.menuType, item: preparedMenuItem },
      });
    });

    blocks.forEach((block) => {
      const contentBlock: ContentBlock = {
        id: block.id,
        type: block.type,
        title: block.title,
        text: block.text,
        orgId: block.orgId,
        workspaceId: block.workspaceId,
        noteId: block.noteId,
        index: block.index,
        place: block.place,
        colspan: block.colspan,
        rowspan: block.rowspan,
        actions: block.actions,
        wrapper: block.wrapper,
        mediaSizes: block.mediaSizes,
        enabled: block.enabled,
        removable: block.removable,
        pageId: block.attr.pageId,
        data: [],
      };
      setChanges({
        type: ChangeEventType.ADD_BLOCK,
        batchId,
        newValue: contentBlock,
        oldValue: undefined,
        meta: { pageTitle: block.attr.pageTitle, pageId: block.attr.pageId, path: block.attr.pagePath },
      });
    });

    return {
      nodes,
      menuItems,
    };
  };
};
